import React from 'react';
import '../common/SharedStyles.css';

// Define the interface for a single definition
interface Definition {
  text: string;
  partOfSpeech?: string;
  examples: string[];
}

interface WordInfoProps {
  word?: string;
  definition?: string;
  definitions?: Definition[];
  phonetics?: string;
  phonics?: string;
  examples?: string[];
  source?: string;
  tags?: string[];
  onRefresh?: () => void;
  refreshing?: boolean;
  className?: string;
}

const WordInfo: React.FC<WordInfoProps> = ({
  word,
  definition,
  definitions,
  phonetics,
  phonics,
  examples,
  source,
  tags,
  onRefresh,
  refreshing = false,
  className = ''
}) => {
  // Check if we have multiple definitions
  const hasMultipleDefinitions = definitions && definitions.length > 0;
  
  return (
    <div className={`word-info ${className}`}>
      {/* Group phonetics and phonics in a pronunciation container */}
      {(phonetics || phonics) && (
        <div className="pronunciation-container">
          {phonetics && (
            <p className="word-phonetics">/{phonetics}/</p>
          )}
          {phonics && (
            <p className="word-phonics">{phonics}</p>
          )}
        </div>
      )}
      
      {/* Display multiple definitions if available */}
      {hasMultipleDefinitions ? (
        <div className="definitions-container">
          {definitions!.map((def, index) => (
            <div key={index} className="definition-section">
              {def.partOfSpeech && (
                <p className="part-of-speech">{def.partOfSpeech}</p>
              )}
              <p className="word-definition">{def.text}</p>
              
              {def.examples && def.examples.length > 0 && (
                <div className="examples-container">
                  <ul className="examples-list">
                    {def.examples.map((example, exIndex) => (
                      <li key={exIndex} className="example-item">{example}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <>
          {/* Fallback to original definition structure */}
          {definition && <p className="word-definition">{definition}</p>}
          
          {examples && examples.length > 0 && (
            <div className="examples-container">
              <ul className="examples-list">
                {examples.map((example, index) => (
                  <li key={index} className="example-item">{example}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
      
      {/* Display tags if available */}
      {tags && tags.length > 0 && (
        <div className="tags-container">
          <div className="tags-list">
            {tags.map((tag, idx) => (
              <span key={idx} className="tag">{tag}</span>
            ))}
          </div>
        </div>
      )}
      
      {/* Source and refresh button moved to bottom */}
      <div className="word-info-footer">
        {source && source !== 'error' && (
          <div className="source-info">
            Source: <span className={`source-${source}`}>{source}</span>
          </div>
        )}
        
        {onRefresh && (
          <button 
            className="refresh-button-subtle"
            onClick={onRefresh}
            title="Refresh definition from OpenAI"
            disabled={refreshing}
          >
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        )}
      </div>
    </div>
  );
};

export default WordInfo; 